import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';
import type { Dispatch } from 'react';

import type { ContractEditorAction, ContractEditorState } from './contractStore';

import { ApiError, QueryKeys, useUpdateContract } from '@/api';

/**
 * The planning autosave (ADR 0010) applied to the active contract: 800 ms
 * after the last commit, PUT `{name, type, status, document, version}` —
 * header edits ride along with document edits. On success only the bumped
 * version is adopted (contracts have no server-assigned fields); 409 parks
 * the editor in `conflict`; other failures back off while edits accumulate.
 */
const DEBOUNCE_MS = 800;
const SUMMARY_INVALIDATE_MIN_MS = 5000;

/**
 * What a flush attempt ended as, with the version a follow-up write must use.
 * `clean` covers both "nothing to save" and "saved" — callers that must not
 * lose work (contract switch, move-category) proceed only on `clean`.
 */
export type FlushResult = { status: 'clean' | 'conflict' | 'error'; version: number };

export const useContractAutosave = (
  projectId: string,
  state: ContractEditorState,
  dispatch: Dispatch<ContractEditorAction>,
) => {
  const mutation = useUpdateContract(projectId);
  const queryClient = useQueryClient();

  const stateRef = useRef(state);
  const mutateRef = useRef(mutation.mutateAsync);
  useEffect(() => {
    stateRef.current = state;
    mutateRef.current = mutation.mutateAsync;
  });

  const inFlightRef = useRef(false);
  const attemptRef = useRef(0);
  const lastSummaryInvalidateRef = useRef(0);

  const flush = useCallback(async (): Promise<FlushResult> => {
    const current = stateRef.current;
    if (current.saveStatus === 'conflict') {
      return { status: 'conflict', version: current.serverVersion };
    }
    if (inFlightRef.current || current.revision === current.savedRevision) {
      // In-flight: the running PUT owns this revision; treat as clean-enough —
      // switch-style callers re-read versions from the cache the PUT updates.
      return { status: 'clean', version: current.serverVersion };
    }

    inFlightRef.current = true;
    const savedRevision = current.revision;
    dispatch({ type: 'SAVE_STARTED' });
    try {
      const response = await mutateRef.current({
        contractId: current.contractId,
        request: {
          name: current.name,
          type: current.type,
          status: current.status,
          document: current.document,
          version: current.serverVersion,
        },
      });
      attemptRef.current = 0;
      dispatch({ type: 'SAVE_SUCCEEDED', version: response.version, savedRevision });
      const now = Date.now();
      if (now - lastSummaryInvalidateRef.current >= SUMMARY_INVALIDATE_MIN_MS) {
        lastSummaryInvalidateRef.current = now;
        void queryClient.invalidateQueries({ queryKey: QueryKeys.ProjectSummary(projectId) });
      }
      return { status: 'clean', version: response.version };
    } catch (error) {
      if (error instanceof ApiError && error.isConflict) {
        dispatch({ type: 'SAVE_CONFLICT' });
        return { status: 'conflict', version: current.serverVersion };
      }
      attemptRef.current += 1;
      dispatch({ type: 'SAVE_FAILED' });
      return { status: 'error', version: current.serverVersion };
    } finally {
      inFlightRef.current = false;
    }
  }, [dispatch, projectId, queryClient]);

  useEffect(() => {
    if (state.saveStatus === 'conflict') return undefined;
    if (state.revision === state.savedRevision) return undefined;
    const delay =
      state.saveStatus === 'error'
        ? Math.min(8000, 2000 * 2 ** Math.max(0, attemptRef.current - 1))
        : DEBOUNCE_MS;
    const timer = setTimeout(() => void flush(), delay);
    return () => clearTimeout(timer);
  }, [state.revision, state.savedRevision, state.saveStatus, flush]);

  return { flush };
};
